import type {
  ChatMessageContent,
  ChatMessage as ChatMessageType,
} from "schemas/generated/chatMessage";

import React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { ImageMessageItem } from "@v2/features/chat/components/chat-elements/chat-message/ImageMessageItem";
import { TextMessageItem } from "@v2/features/chat/components/chat-elements/chat-message/TextMessageItem";
import ToolCallMessageItem from "@v2/features/chat/components/chat-elements/chat-message/ToolMessageItem";
import { VideoMessageItem } from "@v2/features/chat/components/chat-elements/chat-message/VideoMessageItem";
import ElementTypeTag from "@v2/features/modifiers/ElementTypeTag";
import { filterNulls } from "replo-utils/lib/array";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { isChatTokenSegment, parseChatTokens } from "schemas/utils/chat";

type ImageMessageContent = Extract<
  ChatMessageContent[number],
  { type: "image" }
>;

type VideoMessageContent = Extract<
  ChatMessageContent[number],
  { type: "video" }
>;

type MediaMessageContent = ImageMessageContent | VideoMessageContent;

interface MediaMessageItemProps {
  item: MediaMessageContent;
}

const MediaMessageItem = ({ item }: MediaMessageItemProps) => {
  if (item.type === "image") {
    return <ImageMessageItem item={item} />;
  }
  return <VideoMessageItem item={item} />;
};

interface NonMediaMessageItemProps {
  item: Exclude<
    ChatMessageContent[number],
    { type: "image" } | { type: "video" }
  >;
  role: string;
  messageId: string;
}

const NonMediaMessageItem = ({
  item,
  role,
  messageId,
}: NonMediaMessageItemProps) => {
  return exhaustiveSwitch(item)({
    text: (item) => (
      <TextMessageItem item={item} role={role} messageId={messageId} />
    ),
    "tool-call": (item) => <ToolCallMessageItem toolCall={item} />,
    "tool-result": null,
  });
};
interface ChatMessageProps {
  message: ChatMessageType;
}

export const ChatMessage = ({ message }: ChatMessageProps) => {
  const { role, content } = message;

  if (role === "tool") {
    return null;
  }

  const isUser = role === "user";

  // NOTE (Kevin, 2025-08-11): We embed selected element context as a <chat-token> at the beginning of the
  // first text item when the user sends a message. We parse ONLY that first text
  // item to extract the token and show the pill above the bubble (not inline).
  // This keeps backend schemas untouched and avoids system-context injection;
  // the token is purely for frontend display of the selection context.
  const isTextContent = (
    item: ChatMessageContent[number],
  ): item is Extract<ChatMessageContent[number], { type: "text" }> => {
    return item.type === "text";
  };

  const visibleContent = content.filter((item) => {
    if (
      item.type === "text" ||
      item.type === "image" ||
      item.type === "video"
    ) {
      return !item.isHidden;
    }
    return true;
  });

  if (visibleContent.length === 0) {
    return null;
  }

  const elementTypes = (() => {
    return visibleContent
      .filter(isTextContent)
      .map((item) => {
        const segments = parseChatTokens(item.text);
        return filterNulls(
          segments.filter(isChatTokenSegment).map((tokenSegment) => {
            if (tokenSegment.token.type === "selected-element") {
              return {
                elementType: tokenSegment.token.data.elementType,
                id: tokenSegment.id,
              };
            }
            return null;
          }),
        );
      })
      .flat();
  })();

  // NOTE (Kevin, 2025-12-15): Filter media content (images and videos) while maintaining
  // their original order so mixed media displays correctly: [image, video, image, video]
  const mediaContent = visibleContent.filter(
    (item): item is MediaMessageContent => {
      return item.type === "image" || item.type === "video";
    },
  );
  const nonMediaContent = visibleContent.filter(
    (
      item,
    ): item is Exclude<
      ChatMessageContent[number],
      { type: "image" } | { type: "video" }
    > => item.type !== "image" && item.type !== "video",
  );

  if (
    nonMediaContent.length === 0 &&
    mediaContent.length === 0 &&
    elementTypes.length === 0
  ) {
    return null;
  }

  return (
    <div
      className={twMerge(
        "gap-1 w-full flex flex-col min-w-0",
        isUser ? "items-end" : "items-start",
      )}
    >
      {elementTypes.length > 0 && (
        <div className="inline-flex items-center gap-1">
          {elementTypes.map(({ elementType, id }) => (
            <ElementTypeTag key={id} elementType={elementType} />
          ))}
        </div>
      )}

      {mediaContent.length > 0 && (
        <div className="flex gap-2 flex-wrap justify-end">
          {mediaContent.map((item, index) => (
            <MediaMessageItem key={`media-${index}`} item={item} />
          ))}
        </div>
      )}

      {nonMediaContent.map((item, index) => (
        <NonMediaMessageItem
          key={`${item.type}-${index}`}
          item={item}
          role={role}
          messageId={message.id}
        />
      ))}
    </div>
  );
};
